import React, { useEffect, useState, useMemo, Fragment, FormEvent, useContext, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import './ManageForms.css'
import Button from "../components/UI/Button";
import { ASSESSMENT_ID, CLIENT_ID, DELIVERY_ID, Mapping, PUBLIC_INTAKE } from "../constants/formTypes";
import { format, formatDistance, parseISO } from 'date-fns';
import Loader from "../components/UI/Loader";
import { activateForm, createForm, createFormField, createFormVersion, deactivateForm, deleteForm, deleteFormDraft, getForm, getForms, makeFormPrivate, makeFormPublic, regeneratePublicId, updateField, updateForm } from "../services/formsService";
import ErrorMessages from "../components/UI/ErrorMessages";
import Form from "../models/serviceModels/Form";
import Dropdown from "../components/UI/Dropdown";
import Modal from "../components/UI/Modal";
import Input from "../components/UI/Input";
import IconButton from "../components/UI/IconButton";
import Checkbox from "../components/UI/Checkbox";
import PreviewForm from "../components/Form/PreviewForm";
import AssignProgram from "../components/Programs/AssignProgram";
import { MeContext, OrganizationContext } from "../App";
import { FREE_PLAN_TYPE_ID } from "../constants/planTypes";
import WarningMessages from "../components/UI/WarnginMessages";
import { ADMIN_ROLE_ID, CONTRIBUTOR_ROLE_ID } from "../constants/userRoles";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import { configuredRequiredFieldsOrgSettingsEnabled, optionalDateOfBirthEnabled, publicFormsEnabled } from "../utils/features";
import QRCode from "react-qr-code";
import { DATE_ID } from "../constants/fieldTypes";
import { ConfiguredRequiredField, getOrganizationConfiguredRequiredFields } from "../services/organizationService";

interface DraftForm {
    name: string;
    form: Form;
}

interface FormWithDrafts {
    form: Form;
    isOnlyDraft: boolean;
    draftForms: DraftForm[];
}

interface ProgramForms {
    programId?: string;
    programName?: string;
    forms: FormWithDrafts[];
}

const ManageForms = () => {
    const [forms, setForms] = useState<Form[]>([]);
    const [loading, setLoading] = useState(true);
    const [failedToLoad, setFailedToLoad] = useState(false);
    const [creatingForm, setCreatingForm] = useState(false);
    const [includeDeactivated, setIncludeDeactivated] = useState(false);
    const [creatingNewVersion, setCreatingNewVersion] = useState(false);
    const [creatingNewVersionFormVersionId, setCreatingNewVersionFormVerionId] = useState('');
    const [assignForm, setAssignForm] = useState<Form>();
    const [creatingAssessment, setCreatingAssessment] = useState(false);
    const [draftsDisplayed, setDraftsDisplayed] = useState<Form[]>([]);
    const [assignedFormsHidden, setAssignedFormsHidden] = useState<string[]>([]);
    const [creatingPublicIntake, setCreatingPublicIntake] = useState(false);
    const [creatingDelivery, setCreatingDelivery] = useState(false);
    const [creatingClient, setCreatingClient] = useState(false);
    const [error, setError] = useState('');
    const [modalError, setModalError] = useState('');
    const [deletingForm, setDeletingForm] = useState(false);
    const [confirmFormDelete, setConfirmFormDelete] = useState<{ name: string, form: Form, published: boolean }>();
    const [activatingForm, setActivatingForm] = useState(false);
    const [confirmFormActivate, setConfirmFormActivate] = useState<Form>();
    const [deactivatingForm, setDeactivatingForm] = useState(false);
    const [confirmFormDeactivate, setConfirmFormDeactivate] = useState<Form>();
    const [renamingForm, setRenamingForm] = useState(false);
    const [renameForm, setRenameForm] = useState<Form>();
    const [editingFormName, setEditingFormName] = useState('');
    const [viewPublicLink, setViewPublicLink] = useState<Form>();
    const [confirmMakePublic, setConfirmMakePublic] = useState<Form>();
    const [confirmMakePrivate, setConfirmMakePrivate] = useState<Form>();
    const [apiModalError, setApiModalError] = useState<any>();
    const [makingPublic, setMakingPublic] = useState(false);
    const [makingPrivate, setMakingPrivate] = useState(false);
    const [regeneratingPublicLink, setRegeneratingPublicLink] = useState(false);
    const [previewForm, setPreviewForm] = useState<{ form: Form, name?: string }>();
    const [namingNewForm, setNamingNewForm] = useState(false);
    const [newFormDetails, setNewFormDetails] = useState<{ formName: string, formType: 'assessment' | 'delivery' | 'public-intake' }>();
    const [showPublicFormsPaywall, setShowPublicFormsPaywall] = useState(false);
    const location = useLocation();
    const [loadingFormToDelete, setLoadingFormToDelete] = useState(false);
    const [formToDelete, setFormToDelete] = useState<Form>();
    const [copiedPublicLinkFormId, setCopiedPublicLinkFormId] = useState<string | null>(null);
    const organizationContext = useContext(OrganizationContext);
    const [linkForQRCode, setlinkForQRCode] = useState<string>();
    const qrCodeRef = useRef<any>();
    const me = useContext(MeContext);
    const [editThankYouMessage, setEditThankYouMessage] = useState<Form>();
    const [thankYouMessageText, setThankYouMessageText] = useState('');
    const [savingThankYouMessage, setSavingThankYouMessage] = useState(false);
    const [useDefaultThankYouMessage, setUseDefaultThankYouMessage] = useState(true);

    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Build Forms - All Forms"
    }, []);

    useEffect(() => {
        organizationContext.refresh();
    }, []);

    useEffect(() => {
        setCreatingForm(creatingAssessment || creatingDelivery || creatingClient || creatingPublicIntake);
    }, [creatingAssessment, creatingDelivery, creatingClient, creatingPublicIntake])

    const loadForms = (deactivated: boolean) => {
        setLoading(true);
        getForms(deactivated).then(forms => {
            setForms(forms);
        }).catch(e => {
            console.error(e);
            setFailedToLoad(true);
        }).finally(() => {
            setLoading(false);
        });
    };

    useEffect(() => {
        loadForms(includeDeactivated);
    }, [includeDeactivated]);


    useEffect(() => {
        if (!forms || forms.length === 0 || !location.state) {
            return;
        }

        const { publishedForm } = location.state as any;
        if (publishedForm) {
            const existingForm = forms.find(f => f.id === publishedForm.id);
            if (existingForm && !existingForm.programId && existingForm.formTypeId !== CLIENT_ID && existingForm.formTypeId !== PUBLIC_INTAKE) {
                // not already assigned to a program
                setAssignForm(existingForm);
            }

            window.history.replaceState({}, ''); // clearing state
        }
    }, [location, forms]);


    const groupedForms: ProgramForms[] = useMemo(() => {
        const grouping: FormWithDrafts[] = [];
        if (!forms || forms.length === 0) {
            return [];
        }

        const sortedForms = [...forms].sort((a, b) => {
            if (a.draft !== b.draft) {
                return a.draft ? 1 : -1;
            }

            if (a.id !== b.id) {
                return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
            }

            return b.version - a.version;
        });

        for (const form of sortedForms) {
            if (!form.draft) {
                const matchingGroup = grouping.find(g => g.form.id === form.id);
                if (!matchingGroup) {
                    grouping.push({ form, isOnlyDraft: false, draftForms: [] });
                } else if (matchingGroup.isOnlyDraft) {
                    matchingGroup.form = form;
                    matchingGroup.isOnlyDraft = false;
                }
            } else {
                const matchingGroup = grouping.find(g => g.form.id === form.id);
                if (matchingGroup) {
                    matchingGroup.draftForms.push({ name: '', form });
                } else {
                    grouping.push({ form, isOnlyDraft: true, draftForms: [] });
                }
            }
        }

        grouping.sort((a, b) => {
            return parseISO(a.form.updated || a.form.created).getTime() < parseISO(b.form.updated || b.form.created).getTime() ? 1 : -1;
        });

        for (const group of grouping) {
            // first sort it by created so we can get the correct draft number
            group.draftForms.sort((a, b) => {
                return parseISO(a.form.created).getTime() < parseISO(b.form.created).getTime() ? 1 : -1;
            });

            let draftCount = 1;
            for (const draft of group.draftForms) {
                const currentDate = format(parseISO(draft.form.created), 'MM/dd/yyyy');
                draft.name = `Draft ${currentDate} - ${draftCount++}`;
            }

            // now re-sort it by updated and created to get them into the useable order
            group.draftForms.sort((a, b) => {
                return parseISO(a.form.updated || a.form.created).getTime() < parseISO(b.form.updated || b.form.created).getTime() ? 1 : -1;
            });
        }

        const programForms: ProgramForms[] = [];

        for (const formGrouping of grouping) {
            if (formGrouping.form.formTypeId === CLIENT_ID) {
                programForms.push({
                    programId: 'client_form',
                    forms: [formGrouping]
                });
            } else {
                // force program id to be public_intake to make sure they are all assigned together to the same group
                if (formGrouping.form.formTypeId === PUBLIC_INTAKE) {
                    formGrouping.form.programId = 'public_intake';
                }
                
                const existingProgram = programForms.find(pf => pf.programId === formGrouping.form.programId);
                if (existingProgram) {
                    existingProgram.forms.push(formGrouping);
                } else {
                    programForms.push({
                        programId: formGrouping.form.programId,
                        programName: formGrouping.form.programName,
                        forms: [formGrouping]
                    });
                }
            }
        }

        // sort them all by program name
        programForms.sort((a, b) => !a.programName || !b.programName ? 0 : a.programName.toLowerCase().localeCompare(b.programName.toLowerCase()));
        // move public intake form to the top        
        const indexOfPublicIntakeForm = programForms.findIndex(pf => pf.programId === 'public_intake');
        if (indexOfPublicIntakeForm >= 0) {
            const [publicIntakeForm] = programForms.splice(indexOfPublicIntakeForm, 1);
            programForms.unshift(publicIntakeForm);
        }

        // move client form to the top        
        const indexOfClientForm = programForms.findIndex(pf => pf.programId === 'client_form');
        if (indexOfClientForm >= 0) {
            const [clientForm] = programForms.splice(indexOfClientForm, 1);
            programForms.unshift(clientForm);
        }

        // move the unassigned forms to the top
        const indexOfUnassigned = programForms.findIndex(pf => !pf.programId);
        if (indexOfUnassigned >= 0) {
            const [unassignedForms] = programForms.splice(indexOfUnassigned, 1);
            programForms.unshift(unassignedForms);
        }

        return programForms;
    }, [forms]);

    const handleSettingNewFormName = (formType: 'assessment' | 'delivery' | 'public-intake') => {
        setNewFormDetails({
            formName: '',
            formType
        });

        setNamingNewForm(true);
    };

    const handleCopyingPublicLink = (form: Form) => {
        if (!form.publicId) {
            console.warn(`No form public id. Unable to copy. FormId: ${form.id}`);
            return;
        }

        const publicLink = `${process.env.REACT_APP_APP_BASE_URL}/forms/public/${form.publicId}`;
        navigator.clipboard.writeText(publicLink).then(() => {
            setCopiedPublicLinkFormId(form.id);
            setTimeout(() => {
                setCopiedPublicLinkFormId(null);
            }, 3000);
        }).catch(e => {
            console.error(e);
        });
    };

    const handleDownloadQrCode = () => {
        if (!linkForQRCode || !qrCodeRef.current) {
            console.warn('No link set for QR Code viewing and no reference to grab');
            return;
        }

        const svgString = new XMLSerializer().serializeToString(qrCodeRef.current);
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);
        const image = new Image();
        image.src = url;
        image.onload = () => {
            const canvas = document.createElement('canvas');

            canvas.width = image.width + 30;
            canvas.height = image.height + 30;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return;
            }
            
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            const x = (canvas.width / 2) - (image.width / 2);
            const y = (canvas.height / 2) - (image.height / 2);
            ctx.drawImage(image, x, y);
            URL.revokeObjectURL(url);
            const imageUrl = canvas.toDataURL('image/jpg').replace('image/png', 'image/octet-stream');
            const a = document.createElement('a');
            a.download = 'qr-code.jpg';
            a.target = "_blank";
            a.href = imageUrl;
            a.click();
        };
    }

    const handleOnCloseConfirmDeleteForm = () => {
        setConfirmFormDelete(undefined);
        setFormToDelete(undefined);
        setLoadingFormToDelete(false);
    };

    const handleCreatingForm = (e: FormEvent) => {
        e.preventDefault();
        if (!newFormDetails) {
            return;
        }

        if (newFormDetails.formType === 'assessment') {
            handleCreateAssessment(newFormDetails.formName);
        } else if (newFormDetails.formType === 'delivery') {
            handleCreateDelivery(newFormDetails.formName);
        } else if (newFormDetails.formType === 'public-intake') {
            handleCreatingPublicIntakeForm(newFormDetails.formName);
        }
    };


    const getConfiguredDateOfBirthField = async () => {
        if (!configuredRequiredFieldsOrgSettingsEnabled) {
            return undefined;
        }

        const requiredFields = await getOrganizationConfiguredRequiredFields();
        return requiredFields.find(field => field.fieldName === 'dateOfBirth');
    };

    const isConfiguredFieldEffectiveToday = (field: ConfiguredRequiredField | undefined) => {
        if (!field) {
            return false;
        }

        if (!field.effectiveDate) {
            return true;
        }

        return field.effectiveDate <= format(new Date(), 'yyyy-MM-dd');
    };

    const addPublicIntakeDateOfBirth = async (form: Form, required: boolean) => {
        const dateOfBirthField = await createFormField({
            formId: form.id,
            versionId: form.versionId,
            fieldTypeId: DATE_ID,
            label: 'Date of Birth',
            name: 'main_profile_date_of_birth',
            order: 3
        });

        await updateField({
            versionId: form.versionId,
            formId: form.id,
            id: dateOfBirthField.id,
            label: dateOfBirthField.label,
            fieldTypeId: dateOfBirthField.fieldTypeId,
            name: dateOfBirthField.name,
            order: dateOfBirthField.order,
            required,
            sortValues: false,
            options: dateOfBirthField.options?.map(option => ({
                label: option.label,
                value: option.value,
                id: option.id,
                deleted: false
            })) || [],
            schema: dateOfBirthField.schema,
            mask: dateOfBirthField.mask
        });
    };

    const handleCreatingPublicIntakeForm = (formName: string) => {
        setCreatingPublicIntake(true);
        setError('');

        createForm({
            name: formName,
            formTypeId: PUBLIC_INTAKE
        }).then(async form => {
            const configuredDateOfBirthField = await getConfiguredDateOfBirthField();
            const shouldAddDateOfBirth = !optionalDateOfBirthEnabled || !!configuredDateOfBirthField;

            if (shouldAddDateOfBirth) {
                await addPublicIntakeDateOfBirth(
                    form,
                    !optionalDateOfBirthEnabled || isConfiguredFieldEffectiveToday(configuredDateOfBirthField)
                );
            }

            navigate(`/admin/forms/${form.id}/versions/${form.versionId}`);
        }).catch(e => {
            console.error(e);
            setError('Failed to create Public Intake form');
        }).finally(() => {
            setNamingNewForm(false);
            setNewFormDetails(undefined);
            setCreatingPublicIntake(false);
        });
    };

    const handleCreateDelivery = (formName: string) => {
        setCreatingDelivery(true);
        setError('');

        createForm({
            name: formName,
            formTypeId: DELIVERY_ID
        }).then(form => {
            navigate(`/admin/forms/${form.id}/versions/${form.versionId}`);
        }).catch(e => {
            console.error(e);
            setError('Failed to create Outputs and Outcomes form');
        }).finally(() => {
            setNamingNewForm(false);
            setNewFormDetails(undefined);
            setCreatingDelivery(false);
        });
    };

    const handleCreateAssessment = (formName: string) => {
        setCreatingAssessment(true);
        setError('');

        createForm({
            name: formName,
            formTypeId: ASSESSMENT_ID
        }).then(form => {
            navigate(`/admin/forms/${form.id}/versions/${form.versionId}`);
        }).catch(e => {
            console.error(e);
            setError('Failed to create assessment');
        }).finally(() => {
            setNamingNewForm(false);
            setNewFormDetails(undefined);
            setCreatingAssessment(false);
        });
    };

    const handleCreateClient = () => {
        setCreatingClient(true);
        setError('');

        createForm({
            name: 'Client Additional Information',
            formTypeId: CLIENT_ID
        }).then(form => {
            navigate(`/admin/forms/${form.id}/versions/${form.versionId}`);
        }).catch((e) => {
            console.error(e);
            setError('Failed to create client form');
        }).finally(() => {
            setCreatingClient(false);
        });
    };

    const handleOnEdit = (form: Form) => {
        if (form.draft) {
            navigate(`/admin/forms/${form.id}/versions/${form.versionId}`);
        } else {
            setCreatingNewVersion(true);
            setError('');
            setCreatingNewVersionFormVerionId(form.versionId);

            createFormVersion(form.id)
                .then(result => {
                    navigate(`/admin/forms/${result.formId}/versions/${result.versionId}`);
                })
                .catch((e) => {
                    console.error(e);
                    setError('Failed to edit form');
                })
                .finally(() => {
                    setCreatingNewVersion(false);
                    setCreatingNewVersionFormVerionId('');
                });

        }
    };

    const handleOnPreview = (form: Form, name?: string) => {
        setPreviewForm({ form, name });
    };

    const handleOnView = (form: Form) => {
        navigate(`/admin/forms/${form.id}/versions/${form.versionId}`);
    };

    const handleOnAssignStart = (form: Form) => {
        setAssignForm(form);
    };

    const handleOnDelete = (form: { name: string, form: Form, published: boolean }) => {
        if (form.published) {
            setLoadingFormToDelete(true);
            getForm(form.form.id, form.form.versionId, true).then(formResult => {
                setFormToDelete(formResult);
            }).catch(e => {
                console.error(e);
                setModalError('Failed to load form');
            }).finally(() => {
                setLoadingFormToDelete(false);
            });
        }

        setModalError('');
        setConfirmFormDelete(form);
    };

    const handleOnActivate = (form: Form) => {
        setModalError('');
        setConfirmFormActivate(form);
    };

    const handleOnDeactivate = (form: Form) => {
        setModalError('');
        setConfirmFormDeactivate(form);
    };

    const handleOnRename = (form: Form) => {
        setModalError('');
        setEditingFormName(form.name);
        setRenameForm(form);
    };

    const handleConfirmedDelete = () => {
        if (!confirmFormDelete) {
            return;
        }
        setModalError('');
        setDeletingForm(true);
        if (confirmFormDelete.published) {
            deleteForm(confirmFormDelete.form.id).then(() => {
                setConfirmFormDelete(undefined);
                loadForms(includeDeactivated);
            }).catch(e => {
                console.error(e);
                setModalError('Failed to delete form');
            }).finally(() => {
                setDeletingForm(false);
                organizationContext.refresh();
            });
        } else {
            deleteFormDraft({
                formId: confirmFormDelete.form.id,
                versionId: confirmFormDelete.form.versionId
            }).then(() => {
                setConfirmFormDelete(undefined);
                loadForms(includeDeactivated);
            }).catch(e => {
                console.error(e);
                setModalError('Failed to delete form');
            }).finally(() => {
                setDeletingForm(false);
                organizationContext.refresh();
            });
        }
    };

    const handleConfirmedActivate = () => {
        if (!confirmFormActivate) {
            return;
        }
        setModalError('');
        setActivatingForm(true);
        activateForm(confirmFormActivate.id).then(() => {
            setConfirmFormActivate(undefined);
            loadForms(includeDeactivated);
        }).catch(e => {
            console.error(e);
            setModalError('Failed to activate form');
        }).finally(() => {
            setActivatingForm(false);
            organizationContext.refresh();
        });
    };

    const handleConfirmedDeactivate = () => {
        if (!confirmFormDeactivate) {
            return;
        }
        setModalError('');
        setDeactivatingForm(true);
        deactivateForm(confirmFormDeactivate.id).then(() => {
            setConfirmFormDeactivate(undefined);
            loadForms(includeDeactivated);
        }).catch(e => {
            console.error(e);
            setModalError('Failed to deactivate form');
        }).finally(() => {
            setDeactivatingForm(false);
            organizationContext.refresh();
        });
    };

    const handleToggleShowDrafts = (form: Form) => {
        if (draftsDisplayed.some(d => d === form)) {
            setDraftsDisplayed(dd => dd.filter(d => d !== form));
        } else {
            setDraftsDisplayed(dd => [...dd, form]);
        }
    };

    const handleToggleShowAssignedForms = (programId?: string) => {
        if (assignedFormsHidden.includes(programId ?? 'unassigned')) {
            setAssignedFormsHidden([
                ...assignedFormsHidden.filter(f => f !== (programId ?? 'unassigned'))
            ]);
        } else {
            setAssignedFormsHidden([
                ...assignedFormsHidden,
                programId ?? 'unassigned'
            ]);
        }
    };

    const handleCancellingNamingNewForm = () => {
        setNamingNewForm(false);
        setNewFormDetails(undefined);
    };

    const handleSubmitRenameForm = (e: any) => {
        e.preventDefault();
        if (!renameForm) {
            return;
        }

        setModalError('');

        if (!editingFormName) {
            setModalError('Form name is required');
            return;
        }

        setRenamingForm(true);
        updateForm({
            id: renameForm.id,
            name: editingFormName
        }).then(() => {
            setRenameForm(undefined);
            loadForms(includeDeactivated);
        }).catch(e => {
            console.error(e);
            setModalError('Failed to rename form');
        }).finally(() => {
            setRenamingForm(false);
        });
    };

    const getFormGroupingName = (program: ProgramForms) => {
        if (program.programId === 'client_form') {
            return 'Client Additional Information';
        }

        if (program.programId === 'public_intake') {
            return 'Public Intake';
        }

        if (!program.programId) {
            return 'Not Assigned to Program';
        }

        return `Assigned to ${program.programName} Program`;
    };

    const handleCancelConfirmPrivate = () => {
        setApiModalError(undefined);
        setConfirmMakePrivate(undefined);
        setMakingPrivate(false);
    }

    const handleConfirmFormPrivate = (form: Form) => {
        setApiModalError(undefined);
        setConfirmMakePrivate(form);
        setMakingPrivate(false);
    }

    const handleMakeFormPrivate = () => {
        setApiModalError(undefined);
        if (!confirmMakePrivate) {
            console.warn('No form is set to confirm private');
            return;
        }

        setMakingPrivate(true);

        makeFormPrivate(confirmMakePrivate.id).then(r => {
            if (!confirmMakePrivate) {
                return;
            }

            const existingForm = forms.find(f => f.id === confirmMakePrivate.id);
            if (existingForm) {
                existingForm.public = r.public;
                existingForm.publicId = r.publicId
                setForms([
                    ...forms
                ]);
            }
            handleCancelConfirmPrivate();
        }).catch(e => {
            console.error(e);
            setApiModalError(e);
        }).finally(() => {
            setMakingPrivate(false);
        });
    }

    const handleCancelConfirmPublic = () => {
        setApiModalError(undefined);
        setConfirmMakePublic(undefined);
        setMakingPublic(false);
    }

    const handleConfirmFormPublic = (form: Form) => {
        setApiModalError(undefined);
        setConfirmMakePublic(form);
        setMakingPublic(false);
    }

    const handleMakeFormPublic = () => {
        setApiModalError(undefined);
        if (!confirmMakePublic) {
            console.warn('No form is set to confirm public');
            return;
        }

        setMakingPublic(true);

        makeFormPublic(confirmMakePublic.id).then(r => {
            if (!confirmMakePublic) {
                return;
            }

            const existingForm = forms.find(f => f.id === confirmMakePublic.id);
            if (existingForm) {
                existingForm.public = r.public;
                existingForm.publicId = r.publicId
                setForms([
                    ...forms
                ]);
            }
            handleCancelConfirmPublic();
        }).catch(e => {
            console.error(e);
            setApiModalError(e);
        }).finally(() => {
            setMakingPublic(false);
        });
    }

    const handleRegeneratingPublicLink = () => {
        setApiModalError(undefined);

        if (!viewPublicLink) {
            console.warn('No form selected to regenerate a public link');
            return;
        }

        setRegeneratingPublicLink(true);

        regeneratePublicId(viewPublicLink.id).then(r => {
            if (!viewPublicLink) {
                return;
            }

            const existingForm = forms.find(f => f.id === viewPublicLink.id);
            if (existingForm) {
                existingForm.public = r.public;
                existingForm.publicId = r.publicId;

                setForms([
                    ...forms
                ]);
            }

            viewPublicLink.public = r.public;
            viewPublicLink.publicId = r.publicId;

            setViewPublicLink({
                ...viewPublicLink
            });
        }).catch(e => {
            console.error(e);
            setApiModalError(e);
        }).finally(() => {
            setRegeneratingPublicLink(false);
        });
    }

    const handleCancelViewPublicLink = () => {
        setApiModalError(undefined);
        setViewPublicLink(undefined);
        setRegeneratingPublicLink(false);
    };

    const handleViewPublicLink = (form: Form) => {
        setApiModalError(undefined);
        setViewPublicLink(form);
        setRegeneratingPublicLink(false);
    };

    const handleEditThankYouMessage = (form: Form) => {
        setModalError('');
        setThankYouMessageText(form.thankYouMessage || '');
        setUseDefaultThankYouMessage(form.useDefaultThankYouMessage !== false);
        setEditThankYouMessage(form);
    };

    const handleCancelEditThankYouMessage = () => {
        setEditThankYouMessage(undefined);
        setThankYouMessageText('');
        setModalError('');
        setSavingThankYouMessage(false);
        setUseDefaultThankYouMessage(true);
    };

    const handleUseDefaultThankYouMessageChange = (checked: boolean) => {
        setUseDefaultThankYouMessage(checked);
        if (checked) {
            setThankYouMessageText('');
        }
    };

    const handleSubmitThankYouMessage = (e: FormEvent) => {
        e.preventDefault();
        if (!editThankYouMessage) {
            return;
        }

        setModalError('');
        setSavingThankYouMessage(true);

        const messageToSave = useDefaultThankYouMessage ? undefined : (thankYouMessageText || undefined);

        updateForm({
            id: editThankYouMessage.id,
            useDefaultThankYouMessage: useDefaultThankYouMessage,
            thankYouMessage: messageToSave
        }).then(() => {
            const existingForm = forms.find(f => f.id === editThankYouMessage.id);
            if (existingForm) {
                existingForm.useDefaultThankYouMessage = useDefaultThankYouMessage;
                existingForm.thankYouMessage = messageToSave;
                setForms([...forms]);
            }
            handleCancelEditThankYouMessage();
        }).catch(e => {
            console.error(e);
            setModalError('Failed to update thank you message');
        }).finally(() => {
            setSavingThankYouMessage(false);
        });
    };

    const handleOnAssign = (programId: string, programName: string, formId: string) => {
        const existingForm = forms.find(f => f.id === formId);
        if (!existingForm) {
            console.warn('Existing form not found');
            return;
        }

        existingForm.programId = programId;
        existingForm.programName = programName;

        setForms([
            ...forms
        ]);

        setAssignForm(undefined);
    };

    const getDeleteModalTitle = () => {
        return confirmFormDelete?.published ? 'Confirm Delete of Published Form' : 'Confirm Delete of Draft Form';
    };

    if (failedToLoad) {
        return (<div className="section">
            <div className="row justify-content-center">
                <div className="column-auto">
                    Failed to load...
                </div>
            </div>
        </div>);
    }

    const isFreePlan = organizationContext.value?.planTypeId === FREE_PLAN_TYPE_ID;
    const lockDownForms = isFreePlan && (organizationContext.value?.activeFormCount ?? 0) >= 5;

    return (<section id="main-content" tabIndex={-1}>
        <h1>Forms</h1>
        {error && <ErrorMessages messages={[error]} />}
        {lockDownForms && <WarningMessages messages={['Only 5 forms are allowed to be active at once on the Free Plan. Upgrade to a paid plan or deactivate existing forms to create a new form.']} />}
        <div className="row justify-content-between align-items-center margin-bottom-3">
            <div className="column-auto">
                <Dropdown 
                    id="create-form-dropdown"
                    buttonLabel="Create New Form"
                    disabled={creatingForm}
                    optionSize="large"
                    options={[
                        { 
                            label: 'Assessment', 
                            onClick: () => handleSettingNewFormName('assessment'),
                            disabled: lockDownForms
                        },
                        { 
                            label: 'Outputs and Outcomes', 
                            onClick: () => handleSettingNewFormName('delivery'),
                            disabled: lockDownForms
                        },
                        ...(publicFormsEnabled ? [{ 
                            label: isFreePlan ? 'Public Intake (Paid)' : 'Public Intake', 
                            onClick: () => isFreePlan ? setShowPublicFormsPaywall(true) : handleSettingNewFormName('public-intake'),
                            disabled: lockDownForms && !isFreePlan
                        }] : []),
                        ...(forms && !loading && !forms.some(f => f.formTypeId === CLIENT_ID) ? [{ 
                            label: 'Participant Additional Information', 
                            onClick: handleCreateClient
                        }] : [])
                    ]}
                />
            </div>
            <div className="column-auto">
                <div className="row align-items-center">
                    <div className="column-auto">
                        <label htmlFor="include-inactive">Include Inactive Forms</label>
                    </div>
                    <div className="column-auto">
                        <input type="checkbox"
                            id="include-inactive"
                            checked={includeDeactivated}
                            onChange={(e) => setIncludeDeactivated(e.target.checked)} />
                    </div>
                </div>
            </div>
        </div>
        {loading && <Loader />}
        {!loading && (<>
            {(!groupedForms || groupedForms.length === 0) && <div>You haven't created any forms yet.  Select Create Delivery or Create Assessment to begin.</div>}
            {groupedForms && groupedForms.length > 0 && (<>
                {groupedForms.map(gf => {
                    return <Fragment key={gf.programId}>
                        <h2 className="margin-vertical-5 program-name"><IconButton id={`hide-show-assigned-forms-${gf.programId}`}
                            onClick={() => handleToggleShowAssignedForms(gf.programId)}
                            className="small flat margin-end-5"
                            icon={assignedFormsHidden.includes(gf.programId ?? 'unassigned') ? <i className="ri-arrow-down-s-line"></i> : <i className="ri-arrow-up-s-line"></i>} />
                            {getFormGroupingName(gf)}</h2>
                        {!assignedFormsHidden.includes(gf.programId ?? 'unassigned') && <div className="list-item-container">
                            {gf.forms.map(f => {
                                const options = [];                                
                                const canDeletePublishedForm = me.user?.userRoleId === CONTRIBUTOR_ROLE_ID || me.user?.userRoleId === ADMIN_ROLE_ID;

                                options.push({ label: 'Preview', onClick: () => { handleOnPreview(f.form) } });
                                options.push({ label: 'Edit', onClick: () => handleOnEdit(f.form) });
                                options.push({ label: 'Rename', onClick: () => handleOnRename(f.form) });

                                if (!f.isOnlyDraft) {                                    
                                    if (canDeletePublishedForm) {
                                        options.push({ label: 'Delete', onClick: () => handleOnDelete({ name: f.form.name, form: f.form, published: true }) });
                                    }
                                    
                                    if (f.form.formTypeId !== CLIENT_ID && f.form.formTypeId !== PUBLIC_INTAKE) {
                                        options.push({ label: 'Assign to Program', onClick: () => handleOnAssignStart(f.form) });
                                    }

                                                if (f.form.formTypeId === PUBLIC_INTAKE) {
                                                        if (f.form.public) {
                                                            options.push({ label: 'Make Private', onClick: () => handleConfirmFormPrivate(f.form) });
                                                        } else {
                                                            options.push({ label: 'Make Public', onClick: () => handleConfirmFormPublic(f.form) });
                                                        }
                                                        options.push({ label: 'Edit Thank You Message', onClick: () => handleEditThankYouMessage(f.form) });
                                                    }
                                } else {
                                    options.push({ label: 'Delete', onClick: () => handleOnDelete({ name: f.form.name, form: f.form, published: false }) });
                                }

                                if (f.form.formTypeId !== CLIENT_ID && !f.isOnlyDraft) {
                                    if (!f.form.deactivated) {
                                        options.push({ label: 'Deactivate', onClick: () => handleOnDeactivate(f.form) });
                                    }
                                    if (f.form.deactivated && !lockDownForms) {
                                        options.push({ label: 'Activate', onClick: () => handleOnActivate(f.form) });
                                    }
                                }

                                return (
                                    <Fragment key={f.form.id}>
                                        <div
                                            className="list-item form-list-item"
                                            data-testid={`form-row-${f.form.id}`}>
                                            <div className="row justify-content-between">
                                                <div className="column-1">
                                                    <IconButton id={`hide-show-drafts-${f.form.id}`}
                                                        onClick={() => handleToggleShowDrafts(f.form)}
                                                        disabled={f.draftForms.length === 0}
                                                        className="small flat"
                                                        icon={draftsDisplayed.includes(f.form) ? <i className="ri-arrow-up-s-line"></i> : <i className="ri-arrow-down-s-line"></i>} />
                                                </div>
                                                <div className="column-9">
                                                    <div className="row">
                                                        <div className="column">
                                                            {f.form.name} - {Mapping[f.form.formTypeId]}
                                                            {f.isOnlyDraft && <> - <span className="text-bold text-italic">Draft</span></>}
                                                            {f.form.deactivated && <> - <span className="text-bold text-italic">Inactive</span></>}
                                                            {publicFormsEnabled && !f.isOnlyDraft && f.form.formTypeId === PUBLIC_INTAKE && <>
                                                                {!f.form.public && <> - <span className="error-message margin-bottom-0">Not Public</span></>}
                                                                {f.form.public && <> - <span className="success-message margin-bottom-0">Public</span></>}
                                                            </>}
                                                            <div className="text-sub">Modified {formatDistance(parseISO(f.form.updated || f.form.created), new Date(), { addSuffix: true })}</div>
                                                            {!f.isOnlyDraft && <div className="text-sub">{f.draftForms.length} Draft(s)</div>}
                                                        </div>
                                                    </div>
                                                    {publicFormsEnabled && f.form.formTypeId === PUBLIC_INTAKE && !f.isOnlyDraft && <>
                                                        <div
                                                            className="row align-items-center padding-3 margin-top-3 border rounded"
                                                            data-testid={`form-public-link-${f.form.id}`}>
                                                            <div className="column">
                                                                <label>Public Link</label>
                                                                <div data-testid={`form-public-link-value-${f.form.id}`}>{f.form.publicId ? `${process.env.REACT_APP_APP_BASE_URL}/forms/public/${f.form.publicId}` : 'No Link'}</div>
                                                            </div>
                                                            <div className="column-auto">
                                                                <div className="row">
                                                                    <div className="column-auto">
                                                                        <Button id="copy-link"
                                                                            className="small secondary"
                                                                            disabled={!f.form.publicId}
                                                                            text={copiedPublicLinkFormId === f.form.id ? "Copied!" : "Copy"}
                                                                            onClick={() => handleCopyingPublicLink(f.form)} />
                                                                    </div>
                                                                    <div className="column-auto">
                                                                        <Button id="qr-code"
                                                                            className="small secondary"
                                                                            disabled={!f.form.publicId} 
                                                                            text="QR Code"
                                                                            onClick={() => setlinkForQRCode(`${process.env.REACT_APP_APP_BASE_URL}/forms/public/${f.form.publicId}`)} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </>}
                                                </div>
                                                <div className="column">
                                                    <div className="row justify-content-end">
                                                        <div className="column-auto">
                                                            <Dropdown id={`options-dropdown-${f.form.versionId}`}
                                                                options={options}
                                                                optionSize="large"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {draftsDisplayed.includes(f.form) && f.draftForms.map(draft => {
                                            const draftOptions = [
                                                { label: 'Edit', onClick: () => handleOnEdit(draft.form) },
                                                { label: 'Delete', onClick: () => handleOnDelete({ ...draft, published: false }) },
                                                { label: 'Preview', onClick: () => { handleOnPreview(draft.form, draft.name) } }
                                            ];

                                            return (<Fragment key={draft.form.id}>
                                                <div className="list-item form-list-item sub-list-item">
                                                    <div className="row  align-items-center">
                                                        <div className="column-8">
                                                            <div className="row">
                                                                <div className="column">
                                                                    <div className="margin-start-2 text-italic">{draft.name}</div>
                                                                    <div className="text-sub">Modified {formatDistance(parseISO(draft.form.updated || draft.form.created), new Date(), { addSuffix: true })}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="column">
                                                            <div className="row justify-content-end">
                                                                <div className="column-auto">
                                                                    <Dropdown id={`options-dropdown-${draft.form.versionId}`}
                                                                        options={draftOptions}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Fragment>);
                                        })}
                                    </Fragment>
                                );
                            })}
                        </div>}
                    </Fragment>
                })}
            </>)}
        </>)}
        <Modal id="qr-code" visible={linkForQRCode !== undefined} className="small" onClose={() => setlinkForQRCode(undefined)}>
            {linkForQRCode && <>
                <div className="row justify-content-center">
                    <div className="column-auto">
                        <QRCode ref={qrCodeRef} value={linkForQRCode} />
                    </div>
                </div>
                <div className="row margin-top-5">
                    <div className="column">
                        <div className="row justify-content-between">
                            <div className="column-medium-auto">
                                <Button id="close-qr-code"
                                    text="Close"
                                    className="secondary"
                                    onClick={() => setlinkForQRCode(undefined)} />
                            </div>
                            <div className="column-medium-auto">
                                <Button id="confirm-delete"
                                    text="Download QR Code"
                                    onClick={handleDownloadQrCode} />
                            </div>
                        </div>
                    </div>
                </div>
            </>}
        </Modal>
        <Modal id="delete-modal" visible={confirmFormDelete !== undefined} className="small" onClose={handleOnCloseConfirmDeleteForm} title={getDeleteModalTitle()}>
            {confirmFormDelete && (<>
                {loadingFormToDelete && <Loader />}
                {!loadingFormToDelete && <div>
                    {modalError && <ErrorMessages messages={[modalError]} />}
                    <div className="row">
                        <div className="column">
                            <div className="text-bold">{confirmFormDelete.name}</div>
                            {confirmFormDelete.published && formToDelete && <>
                                <br />
                                <div>Deleting this will also delete the following related data:</div>
                                <ul>
                                    <li>Form Submissions (Total = {formToDelete?.stats?.totalSubmissions})</li>
                                    <li>Files Uploaded in Form Submissions (Total = {formToDelete?.stats?.totalFiles})</li>
                                    <li>Form Report Results</li>
                                    <li className="text-bold">All Drafts of this Form</li>
                                </ul>
                            </>}
                            <br />
                            <div>Are you sure you want to delete this form?</div>
                            <div className="text-bold">The change is irreversible.</div>
                        </div>
                    </div>
                    <div className="row margin-top-5">
                        <div className="column">
                            <div className="row justify-content-between">
                                <div className="column-medium-auto">
                                    <Button id="cancel-delete"
                                        text="Cancel"
                                        className="secondary"
                                        disabled={deletingForm}
                                        onClick={handleOnCloseConfirmDeleteForm} />
                                </div>
                                <div className="column-medium-auto">
                                    <Button id="confirm-delete"
                                        text="Delete Form"
                                        className="warning"
                                        disabled={deletingForm || (confirmFormDelete.published && !formToDelete)}
                                        loading={deletingForm}
                                        onClick={handleConfirmedDelete} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>}
            </>
            )}
        </Modal>
        <Modal id="activate-modal" visible={confirmFormActivate !== undefined} className="small" onClose={() => setConfirmFormActivate(undefined)} title="Activate Form">
            {confirmFormActivate && (
                <>
                    {modalError && <ErrorMessages messages={[modalError]} />}
                    <div className="row">
                        <div className="column">
                            <p>Are you sure you want to activate this form?
                                (Users will be able to start submitting this form)
                            </p>
                            <p>
                                "{confirmFormActivate.name}"
                            </p>
                        </div>
                    </div>
                    <div className="row margin-top-5">
                        <div className="column">
                            <div className="row justify-content-between">
                                <div className="column-medium-auto">
                                    <Button id="cancel-activate"
                                        text="Cancel"
                                        className="secondary"
                                        disabled={activatingForm}
                                        onClick={() => setConfirmFormActivate(undefined)} />
                                </div>
                                <div className="column-medium-auto">
                                    <Button id="confirm-activate"
                                        text="Activate"
                                        disabled={activatingForm}
                                        loading={activatingForm}
                                        onClick={handleConfirmedActivate} />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </Modal>
        <Modal id="deactivate-modal" visible={confirmFormDeactivate !== undefined} className="small" onClose={() => setConfirmFormDeactivate(undefined)} title="Deacivate Form">
            {confirmFormDeactivate && (
                <>
                    {modalError && <ErrorMessages messages={[modalError]} />}
                    <div className="row">
                        <div className="column">
                            <p>Are you sure you want to deactivate this form?
                                (Users will NO longer be able to submit the form. Any form in progress might fail to submit.)
                            </p>
                            <p>
                                "{confirmFormDeactivate.name}"
                            </p>
                        </div>
                    </div>
                    <div className="row margin-top-5">
                        <div className="column">
                            <div className="row justify-content-between">
                                <div className="column-medium-auto">
                                    <Button id="cancel-deactivate"
                                        text="Cancel"
                                        className="secondary"
                                        disabled={deactivatingForm}
                                        onClick={() => setConfirmFormDeactivate(undefined)} />
                                </div>
                                <div className="column-medium-auto">
                                    <Button id="confirm-deactivate"
                                        text="Deactivate"
                                        disabled={deactivatingForm}
                                        loading={deactivatingForm}
                                        onClick={handleConfirmedDeactivate} />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </Modal>
        <Modal id="make_private-modal" visible={confirmMakePrivate !== undefined} className="small" onClose={handleCancelConfirmPrivate} title="Make Form Private">
            {confirmMakePrivate && (
                <>
                    <ApiErrorMessages error={apiModalError} />
                    <div className="row">
                        <div className="column">
                            <p>Are you sure you want to make this form private?
                                (Users will NO longer be able to submit the form. And it will NOT be accessible by users outside of Countbubble.)
                            </p>
                            <p>
                                "{confirmMakePrivate.name}"
                            </p>
                        </div>
                    </div>
                    <div className="row margin-top-5">
                        <div className="column">
                            <div className="row justify-content-between">
                                <div className="column-medium-auto">
                                    <Button id="cancel-make-private"
                                        text="Cancel"
                                        className="secondary"
                                        disabled={makingPrivate}
                                        onClick={handleCancelConfirmPrivate} />
                                </div>
                                <div className="column-medium-auto">
                                    <Button id="confirm-deactivate"
                                        text="Make Private"
                                        disabled={makingPrivate}
                                        loading={makingPrivate}
                                        onClick={handleMakeFormPrivate} />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </Modal>
        <Modal id="make-public-modal" visible={confirmMakePublic !== undefined} className="small" onClose={handleCancelConfirmPublic} title="Make Form Public">
            {confirmMakePublic && (
                <>
                    <ApiErrorMessages error={apiModalError} />
                    <div className="row">
                        <div className="column">
                            <p>Are you sure you want to make this form public?
                                (Anyone with the public link to the form will be able to submit this form from outside of Countbubble.)
                            </p>
                            <p>
                                "{confirmMakePublic.name}"
                            </p>
                        </div>
                    </div>
                    <div className="row margin-top-5">
                        <div className="column">
                            <div className="row justify-content-between">
                                <div className="column-medium-auto">
                                    <Button id="cancel-make-public"
                                        text="Cancel"
                                        className="secondary"
                                        disabled={makingPublic}
                                        onClick={handleCancelConfirmPublic} />
                                </div>
                                <div className="column-medium-auto">
                                    <Button id="confirm-deactivate"
                                        text="Make Public"
                                        disabled={makingPublic}
                                        loading={makingPublic}
                                        onClick={handleMakeFormPublic} />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </Modal>
        <Modal id="rename-modal" visible={renameForm !== undefined} className="small" onClose={() => setRenameForm(undefined)} title="Rename Form">
            {renameForm && (
                <form onSubmit={handleSubmitRenameForm}>
                    {modalError && <ErrorMessages messages={[modalError]} />}
                    <div className="row">
                        <div className="column">
                            <label>Current Name:</label>
                            <br />
                            {renameForm.name}
                        </div>
                    </div>
                    <div className="row margin-top-2">
                        <div className="column">
                            <Input id="new-form-name" label="New Name" value={editingFormName} onChange={(e) => setEditingFormName(e)} />
                        </div>
                    </div>
                    <div className="row margin-top-5">
                        <div className="column">
                            <div className="row justify-content-between">
                                <div className="column-medium-auto">
                                    <Button id="cancel-rename"
                                        text="Cancel"
                                        className="secondary"
                                        disabled={renamingForm}
                                        onClick={() => setRenameForm(undefined)} />
                                </div>
                                <div className="column-medium-auto">
                                    <Button id="submit-rename"
                                        text="Rename"
                                        type="submit"
                                        disabled={renamingForm}
                                        loading={renamingForm} />
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            )}
        </Modal>
        <Modal id="preview-form-modal" visible={previewForm !== undefined} className="medium" onClose={() => setPreviewForm(undefined)} title="Preview Form">
            {previewForm && <div className="row justify-content-center">
                <div className="column">
                    {previewForm.name && <i>{previewForm.name}</i>}
                    <PreviewForm formId={previewForm.form.id}
                        versionId={previewForm.form.versionId} />
                </div>
            </div>}
        </Modal>
        <Modal id="assign-to-program-modal" visible={assignForm !== undefined} className="small" onClose={() => setAssignForm(undefined)} title="Assign Form to Program">
            {assignForm && <div className="row justify-content-center">
                <div className="column">
                    {assignForm.name && <div className="margin-vertical-5">Form Name: <i>{assignForm.name}</i></div>}
                    <AssignProgram formId={assignForm.id}
                        onAssigned={(id, name) => handleOnAssign(id, name, assignForm.id)} />
                </div>
            </div>}
        </Modal>
        <Modal id="new-form-name" visible={namingNewForm} className="small" onClose={() => setNamingNewForm(false)} title="Name New Form">
            {newFormDetails && <form onSubmit={handleCreatingForm}>
                {modalError && <ErrorMessages messages={[modalError]} />}
                <div className="row margin-top-2">
                    <div className="column">
                        <Input id="new-form-name"
                            value={newFormDetails.formName}
                            onChange={(value) => setNewFormDetails({ ...newFormDetails, formName: value })}
                            label={`New ${newFormDetails.formType === 'assessment' ? 'Assessment' : newFormDetails.formType === 'delivery' ? 'Outputs and Outcomes' : 'Public In-Take'} Name`} />
                    </div>
                </div>
                <div className="row margin-top-5">
                    <div className="column">
                        <div className="row justify-content-between">
                            <div className="column-medium-auto">
                                <Button id="cancel-rename"
                                    text="Cancel"
                                    className="secondary"
                                    disabled={creatingForm}
                                    onClick={handleCancellingNamingNewForm} />
                            </div>
                            <div className="column-medium-auto">
                                <Button id="submit-rename"
                                    text="Create Form"
                                    type="submit"
                                    disabled={creatingForm}
                                    loading={creatingForm} />
                            </div>
                        </div>
                    </div>
                </div>
            </form>}
        </Modal>
        <Modal id="edit-thank-you-message" visible={editThankYouMessage !== undefined} className="small" onClose={handleCancelEditThankYouMessage} title="Edit Thank You Message">
            {editThankYouMessage && (
                <form onSubmit={handleSubmitThankYouMessage}>
                    {modalError && <ErrorMessages messages={[modalError]} />}
                    <div className="row">
                        <div className="column">
                            <div className="margin-bottom-3 padding-3 border rounded">
                                <label>Default Message:</label>
                                <div className="text-sub">
                                    <strong>Thank you!</strong>
                                    <br />
                                    Your submission is on its way for review.
                                </div>
                            </div>
                            <Checkbox id="use-default-thank-you-message"
                                label="Use default thank you message"
                                checked={useDefaultThankYouMessage}
                                onChange={handleUseDefaultThankYouMessageChange} />
                            <div className="margin-top-3">
                                <Input id="thank-you-message"
                                    type="multi"
                                    label="Custom Thank You Message"
                                    value={thankYouMessageText}
                                    disabled={useDefaultThankYouMessage}
                                    onChange={(e) => setThankYouMessageText(e)} />
                                {!useDefaultThankYouMessage && !thankYouMessageText && (
                                    <div className="text-sub margin-top-2">
                                        Leave blank for no thank you message after submission.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="row margin-top-5">
                        <div className="column">
                            <div className="row justify-content-between">
                                <div className="column-medium-auto">
                                    <Button id="cancel-thank-you-message"
                                        text="Cancel"
                                        className="secondary"
                                        disabled={savingThankYouMessage}
                                        onClick={handleCancelEditThankYouMessage} />
                                </div>
                                <div className="column-medium-auto">
                                    <Button id="save-thank-you-message"
                                        text="Save"
                                        type="submit"
                                        disabled={savingThankYouMessage}
                                        loading={savingThankYouMessage} />
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            )}
        </Modal>
        <Modal id="public-forms-paywall" 
            visible={showPublicFormsPaywall} 
            className="small" 
            title="Paid Feature" 
            onClose={() => setShowPublicFormsPaywall(false)}>
            <div className="row">
                <div className="column">
                    <p>
                        <strong>Public Intake Forms</strong> is a paid feature that allows you to create public intake forms 
                        and collect submissions from anyone with the link.
                    </p>
                    <p>
                        Upgrade to a paid plan to enable Public Intake Forms.
                    </p>
                </div>
            </div>
            <div className="row margin-top-3 justify-content-between">
                <div className="column-auto">
                    <Button id="close-paywall" 
                        text="Close" 
                        className="secondary" 
                        onClick={() => setShowPublicFormsPaywall(false)} />
                </div>
                <div className="column-auto">
                    <Button id="upgrade-plan" 
                        text="View Plans" 
                        className="action" 
                        href="/admin/billing" />
                </div>
            </div>
        </Modal>
    </section>);
};

export default ManageForms;
